import { memo } from "react";
import { Check } from "lucide-react";
import { cn } from "@/shared/lib/utils";

export interface WizardStepDef {
  key: string;
  label: string;
}

interface WizardStepperProps {
  steps: WizardStepDef[];
  current: number;
  /** Maior etapa já alcançada — define quais passos são clicáveis. */
  maxReached: number;
  onStepClick: (index: number) => void;
}

/**
 * Indicador de progresso do wizard de criação de aluguel.
 *
 * Estados por passo: concluído (check), atual (preenchido) e futuro
 * (contorno). Apenas passos já alcançados (`index <= maxReached`) são
 * clicáveis — evita pular validação de etapas anteriores. Os rótulos
 * ficam ocultos no mobile (`sm:`), restando só os números.
 */
export const WizardStepper = memo(function WizardStepper({
  steps,
  current,
  maxReached,
  onStepClick,
}: WizardStepperProps) {
  return (
    <ol className="flex items-center w-full">
      {steps.map((step, index) => {
        const isCompleted = index < current;
        const isCurrent = index === current;
        const isReachable = index <= maxReached;
        const isLast = index === steps.length - 1;

        return (
          <li
            key={step.key}
            className={cn("flex items-center", !isLast && "flex-1")}
          >
            <button
              type="button"
              disabled={!isReachable}
              onClick={() => isReachable && onStepClick(index)}
              aria-current={isCurrent ? "step" : undefined}
              className={cn(
                "flex items-center gap-2",
                isReachable ? "cursor-pointer" : "cursor-not-allowed",
              )}
            >
              <span
                className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full border text-sm font-medium shrink-0 transition-colors",
                  isCurrent &&
                    "border-primary bg-primary text-primary-foreground",
                  isCompleted && "border-primary bg-primary/10 text-primary",
                  !isCurrent &&
                    !isCompleted &&
                    "border-input text-muted-foreground",
                )}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
              </span>
              <span
                className={cn(
                  "text-sm font-medium whitespace-nowrap hidden sm:inline",
                  isCurrent ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            </button>
            {!isLast ? (
              <span
                className={cn(
                  "flex-1 h-px mx-2 sm:mx-3",
                  index < current ? "bg-primary" : "bg-border",
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
});
